import Phaser from 'phaser';
import { TitleScene } from './scenes/TitleScene';
import { GameScene } from './scenes/GameScene';
import { GameOverScene } from './scenes/GameOverScene';
import { UpgradeScene } from './scenes/UpgradeScene';
import { AttractGameScene } from './scenes/AttractGameScene';

declare global {
  interface Window {
    gameInstance: Phaser.Game | null;
    isGameMuted: boolean;
  }
}

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: 1200,
  height: 800,
  parent: 'game-container',
  backgroundColor: '#1a1a2e',
  // Use setTimeout instead of requestAnimationFrame so the game loop runs reliably
  // in headless browser environments (Playwright CI) that may throttle rAF.
  fps: {
    forceSetTimeOut: true,
  },
  physics: {
    default: 'arcade',
    arcade: {
      gravity: { x: 0, y: 0 },
      debug: false,
    },
  },
  scene: [TitleScene, GameScene, UpgradeScene, GameOverScene, AttractGameScene],
};

// Create the game and expose it immediately so the mute button can always find it
const game = new Phaser.Game(config);
window.gameInstance = game;

// Patch the sound manager's `mute` property to use a simple boolean backing store.
// WebAudioSoundManager's built-in getter reads `masterMuteNode.gain.value` which is
// async (AudioContext may be suspended), making it unreliable for synchronous tests.
// We override the instance property so reads are always instant and accurate.
function patchSoundMute(sm: Phaser.Sound.BaseSoundManager) {
  const proto = Object.getPrototypeOf(sm);
  const desc = Object.getOwnPropertyDescriptor(proto, 'mute');
  if (desc && desc.get) {
    // WebAudioSoundManager uses a getter/setter — replace with a cached boolean
    let _muted = false;
    Object.defineProperty(sm, 'mute', {
      get: () => _muted,
      set: (v: boolean) => {
        _muted = v;
        // Still call the original setter to actually mute the audio output
        if (desc.set) desc.set.call(sm, v);
      },
      configurable: true,
      enumerable: true,
    });
  }
  // BaseSoundManager / NoAudioSoundManager use a plain property — no patch needed
}

// The SoundManager exists right after Game construction
if (game.sound) {
  patchSoundMute(game.sound);
}

// Mute button — DOM overlay, persists across all scenes
const muteButton = document.getElementById('mute-button') as HTMLButtonElement;
let isMuted = false;

// Expose mute state for Playwright tests
window.gameInstance = game; // Re-assign to ensure it's available after sound patch

Object.defineProperty(window, 'isGameMuted', {
  get: () => isMuted,
  set: (v: boolean) => {
    isMuted = v;
    muteButton.textContent = isMuted ? '🔇' : '🔊';
    if (game.sound) {
      game.sound.mute = isMuted;
    }
  },
  configurable: true,
});

muteButton?.addEventListener('click', () => {
  window.isGameMuted = !isMuted;
});
